import {
  Card,
  Row,
  Table,
  Col,
  Input,
  message,
  Spin,
  Modal,
  Switch,
  Button,
  Checkbox,
  Typography,
  Form,
  Tooltip,
} from "antd";
import { AiFillDelete } from "react-icons/ai";
import { RiAddBoxFill } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { SearchOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { FaEdit, FaPen, FaTrashAlt } from "react-icons/fa";
import React from "react";
import {
  getAllrole,
  updateRole,
  updateRolePermissions,
  getallMenu,
  deleteRole,
  getallRolePermissions,
  createMenu,
  updateMenu,
} from "../../utils/API"; // ✅ Import API functions
import { hasPermission } from "../../utils/HasPermission";
import Addrolemodal from "./Add";
import Editrolemodal from "./Edit";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { autoRegisterCurrentPageColumns } from "../../utils/emailTemplateColumns";

interface Role {
  _id: string;
  roleId: number;
  name: string;
  isActive: boolean;
  permissions?: {
    menuSlug: string;
    create: boolean;
    update: boolean;
    delete: boolean;
  }[];
}

export const RoleTable = () => {
  const [dataSource, setDataSource] = useState<Role[]>([]);
  const [filteredData, setFilteredData] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  // const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [currentTask, setCurrentTask] = useState<Role | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false); // Add this line
  const [isAddMenuModalOpen, setIsAddMenuModalOpen] = useState(false);
  const [newMenuName, setNewMenuName] = useState("");
  const [editingMenuId, setEditingMenuId] = useState<string | null>(null);
  const [editedMenuName, setEditedMenuName] = useState("");
  const [hoveredMenuId, setHoveredMenuId] = useState<string | null>(null);

  const [permissions, setPermissions] = useState<{
    [menuSlug: string]: {
      all: boolean;
      visible: boolean;
      get: boolean;
      create: boolean;
      update: boolean;
      delete: boolean;
    };
  }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [menuItems, setMenuItems] = useState<
    { _id: string; slug: string; menuName: string; sequance: number }[]
  >([]);
  const [newMenuSequence, setNewMenuSequence] = useState<number>(
    menuItems.length + 1,
  );
  const navigate = useNavigate();

  const handleRename = async (menu: any) => {
    if (!editedMenuName.trim()) {
      message.warning("Menu name cannot be empty");
      return;
    }

    try {
      // ✅ Pass object payload
      await updateMenu(menu._id, {
        menuName: editedMenuName,
      });

      // ✅ Update local state
      setMenuItems((prev) =>
        prev.map((m) =>
          m._id === menu._id ? { ...m, menuName: editedMenuName } : m,
        ),
      );

      message.success("Menu renamed successfully");
    } catch (err) {
      console.error(err);
      message.error("Rename failed");
    } finally {
      setEditingMenuId(null);
    }
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    const reordered = Array.from(menuItems);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // Update sequence numbers
    const updated = reordered.map((item, index) => ({
      ...item,
      sequance: index + 1,
    }));

    setMenuItems(updated);

    try {
      // Persist updated sequence to backend
      await Promise.all(
        updated.map((menu) =>
          updateMenu(menu._id, {
            menuName: menu.menuName,
            sequance: menu.sequance,
          }),
        ),
      );

      message.success("Menu sequence updated!");
    } catch (err) {
      console.error(err);
      message.error("Failed to update sequence");
    }
  };

  const handleAddMenu = () => {
    setIsAddMenuModalOpen(true);
  };

  useEffect(() => {
    const loadMenuItems = async () => {
      try {
        const menus = await getallMenu(""); // Fetch menus
        // console.log("Loaded Menu Items:", menus);

        // ✅ Store _id properly and handle sequence
        const formattedMenus = menus.map((menu: any) => ({
          _id: menu._id, // ✅ Ensure menuId is stored correctly
          slug: menu.slug, // Keep menuSlug for UI usage
          menuName: menu.menuName,
          sequance: menu.sequance !== undefined ? menu.sequance : undefined, // Handle missing sequence
        }));

        setMenuItems(formattedMenus);
      } catch (error) {
        console.error("Error fetching menus:", error);
      }
    };
    getRoles();
    loadMenuItems();
  }, []);

  // ===== REGISTER EMAIL TEMPLATE COLUMNS =====
  useEffect(() => {
    const rolesColumns = [
      { title: "ID", dataIndex: "roleId", key: "roleId" },
      { title: "Role Name", dataIndex: "name", key: "name" },
      { title: "Status", dataIndex: "isActive", key: "isActive" },
    ];
    autoRegisterCurrentPageColumns(rolesColumns);
  }, []);

  //fetche permission
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const responseData = await getallRolePermissions("");
        // console.log("Raw API Response:", responseData);

        if (
          !responseData ||
          !responseData.data ||
          !Array.isArray(responseData.data) ||
          responseData.data.length === 0
        ) {
          console.error("Invalid response format or empty data:", responseData);
          return;
        }

        const rolePermissions = responseData.data[0]?.permissions || []; // Extract permissions safely
        // console.log("Extracted Role Permissions:", rolePermissions);

        // ✅ Store permissions using menuSlug (for UI display)
        const formattedPermissions: {
          [key: string]: {
            all: boolean;
            visible: boolean;
            get: boolean;
            create: boolean;
            update: boolean;
            delete: boolean;
          };
        } = {};
        rolePermissions.forEach((perm: any) => {
          if (perm.menuId && perm.menuId.slug) {
            formattedPermissions[perm.menuId.slug] = {
              all: perm.permissionIds?.all || false,
              visible: perm.permissionIds?.visible || false,
              get: perm.permissionIds?.get || false,
              create: perm.permissionIds?.create || false,
              update: perm.permissionIds?.update || false,
              delete: perm.permissionIds?.delete || false,
            };
          }
        });

        setPermissions(formattedPermissions);
      } catch (error) {
        console.error("Error fetching permissions:", error);
        message.error("Failed to load permissions.");
      }
    };

    loadPermissions();
  }, []);

  //  Fetch Roles from API
  const getRoles = async () => {
    setLoading(true);
    try {
      const roles = await getAllrole();
      // console.log("Fetched Roles:", roles);

      if (roles.length > 0) {
        const sortedData = roles
          .sort((a: { _id: number }, b: { _id: number }) =>
            b._id > a._id ? 1 : -1,
          ) // ✅ Sorting by `_id`, latest first
          .map((role: any, index: number) => ({
            ...role,
            serialNumber: index + 1, // ✅ Assign serial number dynamically
          }));

        setDataSource(sortedData);
        setFilteredData(sortedData);
      } else {
        setDataSource([]);
        setFilteredData([]);
        message.error("No roles found.");
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  //  Handle Active/Inactive Toggle
  const handleToggleStatus = async (roleId: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      const payload = { isActive: newStatus }; //  Ensure isActive is explicitly sent

      await updateRole(roleId, payload); //  Send proper payload
      message.success(
        `Role ${newStatus ? "Activated" : "Deactivated"} successfully!`,
      );

      setDataSource((prev) =>
        prev.map((role) =>
          role._id === roleId ? { ...role, isActive: newStatus } : role,
        ),
      );
      setFilteredData((prev) =>
        prev.map((role) =>
          role._id === roleId ? { ...role, isActive: newStatus } : role,
        ),
      );
    } catch (error) {
      console.error("Error updating role status:", error);
      message.error("Failed to update role status.");
    }
  };

  const handleDeleteConfirmation = (id: string) => {
    setDeleteId(id);
  };

  // Handle Delete Action
  const handleDelete = async () => {
    if (deleteId) {
      try {
        const response = await deleteRole(deleteId);
        if (response) {
          message.success("Task deleted successfully");
          getRoles(); // Refresh list
        } else {
          message.error("Failed to delete task");
        }
      } catch (error) {
        message.error("Error deleting task  . Please try again.");
      } finally {
        setDeleteId(null); // Close the confirmation modal
      }
    }
  };

  //  Handle Search Filtering
  useEffect(() => {
    const filtered = dataSource.filter((role) =>
      role.name.toLowerCase().includes(searchText.toLowerCase()),
    );
    setFilteredData(filtered);
  }, [searchText, dataSource]);

  //  Handle Open Permission Modal
  const handleOpenPermissions = async (role: Role) => {
    setSelectedRole(role);
    setIsModalOpen(true);
    setLoading(true); // Show loading spinner

    try {
      const responseData = await getallRolePermissions("");
      // console.log("Fetched Permissions for Role:", responseData);

      if (
        !responseData ||
        !responseData.data ||
        !Array.isArray(responseData.data) ||
        responseData.data.length === 0
      ) {
        console.error("Invalid response format or empty data:", responseData);
        setPermissions({});
        return;
      }

      const rolePermissions =
        responseData.data.find((r: any) => r.roleId._id === role._id)
          ?.permissions || [];

      const formattedPermissions: {
        [key: string]: {
          all: boolean;
          visible: boolean;
          get: boolean;
          create: boolean;
          update: boolean;
          delete: boolean;
        };
      } = {};
      rolePermissions.forEach((perm: any) => {
        if (perm.menuId && perm.menuId.slug) {
          formattedPermissions[perm.menuId.slug] = {
            all: perm.permissionIds?.all || false,
            visible: perm.permissionIds?.visible || false,
            get: perm.permissionIds?.get || false,
            create: perm.permissionIds?.create || false,
            update: perm.permissionIds?.update || false,
            delete: perm.permissionIds?.delete || false,
          };
        }
      });
      setPermissions(formattedPermissions);
    } catch (error) {
      console.error("Error fetching role permissions:", error);
      message.error("Failed to fetch permissions.");
    } finally {
      setLoading(false);
    }
  };

  //  Handle Checkbox Change
  // console.log("permission", permissions);
  const handleCheckboxChange = (
    menuSlug: string,
    permissionType: "all" | "visible" | "get" | "create" | "update" | "delete",
    checked: boolean,
  ) => {
    setPermissions((prev) => ({
      ...prev,
      [menuSlug]: {
        ...prev[menuSlug],
        [permissionType]: checked,
      },
    }));
  };

  //Handle Submit permission
  const handleSubmitPermissions = async () => {
    if (!selectedRole) {
      message.error("No role selected.");
      return;
    }

    setLoading(true); // Show loading state

    try {
      const responseData = await getallRolePermissions(""); // Fetch role permissions from API
      // console.log("🔍 API Response:", responseData);

      if (
        !responseData ||
        !responseData.data ||
        !Array.isArray(responseData.data)
      ) {
        // console.error("Invalid API response format:", responseData);
        message.error("Failed to fetch role permissions.");
        setLoading(false);
        return;
      }

      // ✅ Find role permissions document by `_id`
      const roleData = responseData.data.find(
        (r: any) => r.roleId._id === selectedRole._id,
      );

      if (!roleData) {
        // console.error("⚠️ Role not found in API response:", responseData.data);
        message.error("Role data not found. Please refresh and try again.");
        setLoading(false);
        return;
      }

      // console.log("✅ Found Role Data:", roleData);

      // ✅ Sort menu items by sequence (items without sequence go last)
      const sortedMenuItems = [...menuItems].sort((a, b) => {
        const aSeq =
          a.sequance !== undefined ? a.sequance : Number.MAX_SAFE_INTEGER;
        const bSeq =
          b.sequance !== undefined ? b.sequance : Number.MAX_SAFE_INTEGER;
        return aSeq - bSeq;
      });

      // ✅ Ensure **all** menu items are included in the payload
      const allPermissions = sortedMenuItems.map((menu) => ({
        menuId: menu._id, // ✅ Always include `menuId`
        permissionIds: {
          all: permissions[menu.slug]?.all ?? false,
          visible: permissions[menu.slug]?.visible ?? false,
          get: permissions[menu.slug]?.get ?? false,
          create: permissions[menu.slug]?.create ?? false,
          update: permissions[menu.slug]?.update ?? false,
          delete: permissions[menu.slug]?.delete ?? false,
        },
      }));

      // ✅ Final payload (with all menu items)
      const payload = {
        _id: roleData._id, // ✅ Use `_id` for the API call
        permissions: allPermissions, // ✅ Send **all** menu items, updated or not
        isActive: selectedRole.isActive,
        isDeleted: false,
      };

      // console.log("🚀 Final Payload:", payload);

      // ✅ Use `_id` in the API URL instead of `roleID`
      await updateRolePermissions(roleData._id, payload);
      message.success("Permissions updated successfully!");
      setIsModalOpen(false);
      getRoles(); // Refresh role list
    } catch (error) {
      console.error("❌ Error updating permissions:", error);
      message.error("Failed to update permissions.");
    } finally {
      setLoading(false);
    }
  };
  // Update the handleEdit function
  const handleEdit = (record: Role) => {
    setCurrentTask(record); // Set the current task to edit
    setIsEditModalVisible(true); // Open the modal
    getRoles();
  };
  return (
    <div>
      <Row
        className="m-2"
        align="middle"
        justify="space-between"
        gutter={[16, 16]}
      >
        {/* Left Side - Heading */}
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 className="headername">Roles & Permission</h2>
        </Col>

        <Col xs={24} md={10}>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            {/* Search Input (Flexible Width) */}
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="flex-1"
              style={{ height: "33px" }}
            />
            {/* Add Role Button */}
            <Button
              type="primary"
              onClick={() => setIsAddTaskModalOpen(true)}
              className="allbuttons"
            >
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add Roles</span>
            </Button>

            <Addrolemodal
              visible={isAddTaskModalOpen}
              onClose={() => setIsAddTaskModalOpen(false)}
              onTaskCreated={() => {
                setIsAddTaskModalOpen(false);
                getRoles(); // Refresh the list after adding
              }}
            />
          </div>
        </Col>
      </Row>

      {/* Role Table */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card className=" w-100">
            <Spin spinning={loading}>
              <div className="overflow-x-auto">
                <Table
                  columns={[
                    { title: "ID", dataIndex: "roleId", key: "roleId" },
                    { title: "Role Name", dataIndex: "name", key: "roleId" },
                    {
                      title: "Permissions",
                      dataIndex: "permissions",
                      key: "permissions",
                      render: (_: any, record: Role) => (
                        <Button
                          type="link"
                          onClick={() => handleOpenPermissions(record)}
                        >
                          Manage Permissions
                        </Button>
                      ),
                    },
                    {
                      title: "Status",
                      dataIndex: "isActive",
                      key: "isActive",
                      render: (isActive: boolean, record: Role) => (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <label
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: 40,
                              height: 22,
                              backgroundColor: isActive ? "#001529" : "#f0f0f0",
                              borderRadius: 13,
                              cursor: "pointer",
                              transition: "background-color 0.3s ease",
                              boxShadow: isActive
                                ? "inset 0 0 0 2px #001529"
                                : "inset 0 0 0 1px #ccc",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isActive}
                              onChange={() =>
                                handleToggleStatus(record._id, isActive)
                              }
                              style={{ display: "none" }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                width: 21,
                                height: 19,
                                left: isActive ? "calc(99% - 21px)" : 2,
                                top: 1,
                                backgroundColor: "#ffffff",
                                borderRadius: "50%",
                                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                                transition:
                                  "left 0.3s ease, box-shadow 0.3s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                fontWeight: "bold",
                                color: isActive ? "#001529" : "#666",
                              }}
                            >
                              {isActive ? "ON" : "OFF"}
                            </span>
                          </label>
                        </div>
                      ),
                    },

                    ...(hasPermission("roles-permissions", "update") ||
                    hasPermission("roles-permissions", "delete")
                      ? [
                          {
                            title: "Action",
                            dataIndex: "action",
                            key: "action",
                            render: (_: any, record: Role) => (
                              <div
                                className="d-flex"
                                style={{ gap: "8px", alignItems: "center" }}
                              >
                                {/* Edit Icon Button */}
                                {hasPermission(
                                  "roles-permissions",
                                  "update",
                                ) && (
                                  <Tooltip title="Edit">
                                    <button
                                      onClick={() => handleEdit(record)}
                                      className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 
                                      shadow-[0_4px_12px_rgba(139,92,246,0.3)] 
                                      hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] 
                                      active:translate-y-[1px] active:shadow-none 
                                     text-white flex items-center justify-center 
                                      transition-all duration-200 ease-in-out group"
                                    >
                                      <FaPen
                                        className="group-hover:scale-110 transition-transform duration-200"
                                        size={12}
                                      />
                                    </button>
                                  </Tooltip>
                                )}

                                {/* Delete Icon Button */}
                                {hasPermission(
                                  "roles-permissions",
                                  "delete",
                                ) && (
                                  <Tooltip title="Delete">
                                    <button
                                      onClick={() =>
                                        handleDeleteConfirmation(record._id)
                                      }
                                      className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 
               shadow-[0_4px_12px_rgba(244,63,94,0.3)] 
               hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] 
               active:translate-y-1 active:shadow-none 
               text-white flex items-center justify-center 
               transition-all duration-200 group"
                                    >
                                      <FaTrashAlt
                                        className="group-hover:scale-110 transition-transform"
                                        size={12}
                                      />
                                    </button>
                                  </Tooltip>
                                )}
                              </div>
                            ),
                          },
                        ]
                      : []),
                  ]}
                  dataSource={filteredData}
                  rowKey="_id"
                  scroll={{ x: "max-content" }} // Ensures table is scrollable on small screens
                />
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Permissions Modal */}
      <Modal
        title={
          <h2
            className="text-center font-bold"
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Manage Permissions
          </h2>
        }
        open={isModalOpen}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <div className="flex justify-between items-center mb-2">
            <div style={{ marginLeft: "8px" }}>
              <button
                key="submit"
                onClick={handleAddMenu}
                className="allsubmitebuttons"
              >
                + Add Menu
              </button>
            </div>
            <div className="flex gap-3" style={{ marginLeft: "0px" }}>
              <Button
                key="cancel"
                onClick={() => setIsModalOpen(false)}
                className="h-10 px-6 text-gray-800 hover:text-gray-600 border-gray-300 hover:border-gray-400"
                style={{
                  height: "40px",
                  padding: "0 24px",
                  borderRadius: "6px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <div style={{ marginRight: "8px" }}>
                <button
                  key="submit"
                  onClick={handleSubmitPermissions}
                  className="allsubmitebutton"
                  style={{
                    width: "100%",
                    height: "40px",
                    padding: "0 24px",
                    borderRadius: "6px",
                    fontWeight: 500,
                  }}
                >
                  Update
                </button>
              </div>
            </div>
          </div>,
        ]}
        width={700}
        style={{ top: 20 }}
      >
        <div className="element">
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto p-4">
            {/* Table Header */}
            <Row className="mb-3 font-bold bg-gray-100 p-2 rounded">
              <Col span={6}>Menu Item</Col>
              <Col span={3} className="text-center">
                All
              </Col>
              <Col span={3} className="text-center">
                Visible
              </Col>
              <Col span={3} className="text-center">
                Get
              </Col>
              <Col span={3} className="text-center">
                Create
              </Col>
              <Col span={3} className="text-center">
                Update
              </Col>
              <Col span={3} className="text-center">
                Delete
              </Col>
            </Row>

            {/* ✅ Drag & Drop + Inline Edit */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="menuList">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef}>
                    {menuItems
                      .sort((a, b) => {
                        const aSeq =
                          a.sequance !== undefined
                            ? a.sequance
                            : Number.MAX_SAFE_INTEGER;
                        const bSeq =
                          b.sequance !== undefined
                            ? b.sequance
                            : Number.MAX_SAFE_INTEGER;
                        return aSeq - bSeq;
                      })
                      .map((menu, index) => (
                        <Draggable
                          key={menu.slug}
                          draggableId={menu.slug}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <Row
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              onMouseEnter={() => setHoveredMenuId(menu._id)}
                              onMouseLeave={() => setHoveredMenuId(null)}
                              className={`mb-3 p-2 border-b last:border-0 flex items-center transition-all duration-150 ${
                                snapshot.isDragging
                                  ? "bg-gray-50 shadow-sm"
                                  : ""
                              }`}
                            >
                              <Col
                                span={6}
                                className="font-medium flex items-center relative"
                              >
                                <span className="cursor-move mr-2 text-gray-400">
                                  ☰
                                </span>

                                {editingMenuId === menu._id ? (
                                  <Input
                                    value={editedMenuName}
                                    onChange={(e) =>
                                      setEditedMenuName(e.target.value)
                                    }
                                    onBlur={() => handleRename(menu)}
                                    onPressEnter={(e) =>
                                      (e.target as HTMLInputElement).blur()
                                    }
                                    size="small"
                                    autoFocus
                                    style={{ width: "80%" }}
                                  />
                                ) : (
                                  <div className="flex items-center space-x-1">
                                    <span className="truncate max-w-[120px]">
                                      {menu.menuName}
                                    </span>

                                    {/* show pen only when hovered (or when editing) */}
                                    {(hoveredMenuId === menu._id ||
                                      editingMenuId === menu._id) && (
                                      <FaPen
                                        className="ml-2 cursor-pointer transition-all duration-200 hover:scale-110"
                                        size={12}
                                        style={{
                                          color: "#001529",
                                        }}
                                        onClick={() => {
                                          setEditingMenuId(menu._id);
                                          setEditedMenuName(menu.menuName);
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                              </Col>

                              {(
                                [
                                  "all",
                                  "visible",
                                  "get",
                                  "create",
                                  "update",
                                  "delete",
                                ] as const
                              ).map((perm) => (
                                <Col
                                  span={3}
                                  key={perm}
                                  className="text-center"
                                >
                                  <Checkbox
                                    checked={
                                      permissions[menu.slug]?.[perm] || false
                                    }
                                    onChange={(e) =>
                                      handleCheckboxChange(
                                        menu.slug,
                                        perm,
                                        e.target.checked,
                                      )
                                    }
                                  />
                                </Col>
                              ))}
                            </Row>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        </div>
        <div className="footer-modals " />
      </Modal>

      {/* Add New Menu */}
      <Modal
        title={
          <div
            style={{
              color: "#4b5563",
              fontFamily: "inter, sans-serif",
              fontSize: "20px",
              fontWeight: 500,
            }}
          >
            Add New Menu
          </div>
        }
        open={isAddMenuModalOpen}
        onCancel={() => {
          setNewMenuName("");
          setIsAddMenuModalOpen(false);
        }}
        onOk={async () => {
          try {
            const res = await createMenu(newMenuName, newMenuSequence);
            const newMenu = res.menu;

            setMenuItems((prev) => [...prev, newMenu]);
            setPermissions((prev) => ({
              ...prev,
              [newMenu.slug ||
              newMenu.menuName.toLowerCase().replace(/\s+/g, "-")]: {
                all: false,
                visible: false,
                get: false,
                create: false,
                update: false,
                delete: false,
              },
            }));

            setNewMenuName("");
            setNewMenuSequence(menuItems.length + 1);
            setIsAddMenuModalOpen(false);
            message.success("Menu added successfully!");
          } catch (error: any) {
            message.error(
              error.response?.data?.message || "Failed to create menu",
            );
          }
        }}
        okText="Create"
        okButtonProps={{
          className:
            "bg-[#001529] text-white hover:!bg-[#001529] hover:!text-white border-none mb-1 me-1",
        }}
        width={500} // Increased width for better spacing
        style={{
          // top: 20,
          maxHeight: "100%",
          margin: "0 auto",
          overflow: "hidden",
          position: "relative",
        }}
        bodyStyle={{
          maxHeight: "calc(90vh - 55px)", // Adjusted for header
          overflowY: "auto",
          padding: "0.25px",
          fontFamily: "inter, sans-serif",
          // fontWeight: 500
        }}
        styles={{
          header: {
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "#f1f5ffff",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
        }}
      >
        <Form
          style={{ flex: 1 }}
          labelCol={{ span: 6 }}
          wrapperCol={{ span: 19 }}
          colon={false}
        >
          <div className="elements">
            <Form.Item
              className="Inputlabel"
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Menu Name <span className="text-danger">*</span>
                </span>
              }
            >
              <Input
                placeholder="Enter menu name"
                value={newMenuName}
                onChange={(e) => setNewMenuName(e.target.value)}
                size="large" // Larger input
                className="Allinput"
              />
            </Form.Item>
            <Form.Item
              className="Inputlabel"
              label={
                <span
                  style={{
                    color: "#4b5563",
                    fontFamily: "inter, sans-serif",
                    fontSize: "14px",
                    paddingLeft: "15px",
                    fontWeight: 500,
                  }}
                >
                  Sequence <span className="text-danger">*</span>
                </span>
              }
            >
              <Input
                type="number"
                min={1}
                placeholder="Enter sequence number"
                value={newMenuSequence}
                onChange={(e) => setNewMenuSequence(Number(e.target.value))}
                size="large"
                className="Allinput"
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId} // <-- This makes the modal open when deleteId has a value
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        // footer={null}
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this Role?</p>
      </Modal>

      {currentTask && (
        // In your parent component where you render EditCustomersModal:
        <Editrolemodal
          visible={isEditModalVisible}
          customerId={currentTask?._id} // Make sure this is passed correctly
          onClose={() => setIsEditModalVisible(false)}
          onSuccess={() => {
            setIsEditModalVisible(false);
            getRoles();
          }}
        />
      )}
    </div>
  );
};

export const ROLES_TABLE_COLUMNS = ["roleId", "name", "isActive"];
